import { parseBotAvatarValue } from "@rakazo/contracts";
import type { LogoAvatarActivity } from "@rakazo/core";
import { logoAvatarEyeInk, logoAvatarTraits } from "@rakazo/core";
import { tokens } from "@rakazo/ui-tokens";
import { memo, useMemo } from "react";
import { cn } from "./lib/utils.js";

// Allspice blob language: one organic silhouette per identity, a flat saturated
// fill, and two small tilted pill eyes. Replaces the upstream layered body; the
// props contract and data attributes are kept so BotAvatar and CSS stay as-is.
const BLOBS = [
  // a soft teardrop, heavier on one side
  "M16 7 C25 2 37 7 40 16 C43 25 40 36 31 40 C22 44 10 41 8 31 C6.5 24 9 11 16 7 Z",
  // a cloud with three top lobes
  "M10 28 C5 23 9 15 16 16 C17 9 24 5 29 9 C35 5 42 11 40 18 C45 22 43 30 37 32 C37 39 29 43 23 41 C15 43 10 35 10 28 Z",
  // a squashed pebble
  "M12 13 C19 5 34 5 39 13 C44 21 43 32 36 38 C28 44 14 43 9 34 C5 27 7 19 12 13 Z",
  // a soft star
  "M24 4 C27 9 31 11 36 9 C35 14 37 18 42 20 C37 23 36 27 38 32 C33 31 29 34 28 39 C25 35 20 35 16 38 C16 33 13 30 8 30 C11 26 11 22 8 18 C13 18 16 15 16 10 C20 12 22 9 24 4 Z",
  // a rounded square-ish pebble
  "M12 15 C12 8 19 5 25 6 C32 7 39 10 40 18 C41 27 38 37 29 40 C21 43 11 39 10 30 C9.5 25 11 20 12 15 Z",
];

function blobIndex(seed: string): number {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  return Math.abs(h) % BLOBS.length;
}

export const LogoAvatar = memo(function LogoAvatar({
  activity = "idle",
  size = 38,
  avatarValue,
  identity,
  label,
  motion = true,
  className,
}: {
  activity?: LogoAvatarActivity;
  size?: number;
  avatarValue?: string;
  identity?: string;
  label?: string;
  motion?: boolean;
  className?: string;
}) {
  const parsed = useMemo(
    () => (avatarValue ? parseBotAvatarValue(avatarValue) : null),
    [avatarValue],
  );
  const traits = useMemo(
    () =>
      logoAvatarTraits(
        identity || (parsed && "color" in parsed ? parsed.color : "default"),
        parsed && "color" in parsed ? parsed.color : undefined,
      ),
    [identity, parsed],
  );
  const eyeInk = logoAvatarEyeInk(traits.color, tokens);
  const body = BLOBS[blobIndex(identity || traits.color)];
  const canMove = motion && size >= 24;
  return (
    <svg
      viewBox="0 0 48 48"
      width={size}
      height={size}
      className={cn("rakazo-logo-avatar shrink-0", className)}
      data-activity={activity}
      data-working={activity !== "idle" && activity !== "error"}
      data-vector-body="true"
      data-motion={canMove}
      data-small={size < 24}
      data-compact={size <= 38}
      data-color={traits.color}
      role={label ? "img" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
    >
      <path d={body} fill={traits.color} />
      <g fill={eyeInk}>
        <rect x="17" y="19.5" width="3.6" height="8.4" rx="1.8" transform="rotate(14 18.8 23.7)" />
        <rect x="26.6" y="18.4" width="3.6" height="8.4" rx="1.8" transform="rotate(14 28.4 22.6)" />
      </g>
    </svg>
  );
});
